import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Download, Printer, FileText, Sparkles, Loader2,
  Mail, Phone, Briefcase, Globe2, TrendingUp, Users, Pencil, Save, FileCheck,
  RefreshCw, Facebook, Instagram, Linkedin, ExternalLink, Heart, MessageCircle, Share2,
  X as XIcon, ImageIcon, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatAuDateTime, formatPeriodLong } from "@/lib/dateAu";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, LabelList,
} from "recharts";
import { BudgetTotals, BudgetTile, RoiSummary } from "@/components/report/BudgetSection";
import { AhrefsBreakdowns } from "@/components/AhrefsBreakdowns";
import { MetaBreakdowns } from "@/components/MetaBreakdowns";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Module-scope chart-config refs — avoid reallocating on every render.
const VRP_BAR_MARGIN = { top: 16, right: 8, left: 0, bottom: 0 };
const VRP_AXIS_TICK = { fontSize: 10, fill: "#71717A" };

const PLATFORM_LABELS = {
  meta: "Meta", facebook: "Facebook", instagram: "Instagram",
  google_ads: "Google Ads", youtube: "YouTube", x_twitter: "X / Twitter",
  linkedin: "LinkedIn", tiktok: "TikTok", pinterest: "Pinterest",
  bing_ads: "Bing Ads", google_analytics: "Google Analytics",
  google_search_console: "Google Search Console", bing_analytics: "Bing Webmaster",
  awstats: "AWStats", openai_ads: "OpenAI Ads (ChatGPT)", worklogs: "Worklogs",
};

const fmtBig = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return "—";
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  if (Math.abs(v) >= 1) return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return v.toFixed(2);
};

export default function ViewReportPage() {
  const { clientId, reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({
    summary_md: null, next_month_focus_md: null,
    anecdotal_notes: null, platform_recommendations: {},
    // Social posts admin edits — exclude individual posts and / or
    // override the caption shown in the PDF.
    social_posts_excluded_ids: null,
    social_posts_caption_overrides: null,
  });
  const [saving, setSaving] = useState(false);
  const [creatingPdf, setCreatingPdf] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/reports/${reportId}/view`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch(() => { if (!cancelled) setReport(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportId]);

  const primary = report?.brand_colors?.primary || "#002FA7";
  const brandFonts = report?.brand_fonts || {};
  // SECURITY: brand fonts are admin-controlled but stored in the DB, so we
  // still sanitize before interpolating into a <style> block to defeat CSS
  // injection (`}; @import url(evil); /*`-style attacks). Allow only letters,
  // numbers, spaces, hyphens, single/double quotes and commas — the legal
  // characters in a CSS font-family list. Weights are clamped to integers in
  // the valid CSS range (100–900).
  const sanitizeFontFamily = (s) =>
    String(s || "").replace(/[^A-Za-z0-9 ,'"\-]/g, "").trim().slice(0, 200);
  const sanitizeWeight = (w, fallback) => {
    const n = parseInt(w, 10);
    if (Number.isFinite(n) && n >= 100 && n <= 900) return n;
    return fallback;
  };
  const brandFontFamily = sanitizeFontFamily(brandFonts.family);
  const headingWeight = sanitizeWeight(brandFonts.heading_weight, 800);
  const subheadingWeight = sanitizeWeight(brandFonts.subheading_weight, 700);
  const bodyWeight = sanitizeWeight(brandFonts.body_weight, 400);
  // Inject scoped CSS to override the report's font family + role weights.
  // Scoping via data attribute keeps the rest of the admin app untouched.
  const brandFontsCss = brandFontFamily
    ? `
      [data-brand-report="${reportId}"],
      [data-brand-report="${reportId}"] *:not(svg):not(svg *):not(.font-mono):not(.font-mono *) {
        font-family: ${brandFontFamily} !important;
      }
      [data-brand-report="${reportId}"] h1,
      [data-brand-report="${reportId}"] h2,
      [data-brand-report="${reportId}"] .font-extrabold,
      [data-brand-report="${reportId}"] [data-role="heading"] {
        font-weight: ${headingWeight} !important;
      }
      [data-brand-report="${reportId}"] h3,
      [data-brand-report="${reportId}"] .font-display.font-bold,
      [data-brand-report="${reportId}"] [data-role="subheading"] {
        font-weight: ${subheadingWeight} !important;
      }
      [data-brand-report="${reportId}"] p,
      [data-brand-report="${reportId}"] td,
      [data-brand-report="${reportId}"] [data-role="body"] {
        font-weight: ${bodyWeight} !important;
      }
      [data-brand-report="${reportId}"] .label-overline,
      [data-brand-report="${reportId}"] .text-xs,
      [data-brand-report="${reportId}"] .text-\\[11px\\],
      [data-brand-report="${reportId}"] [data-role="small"] {
        font-weight: ${sanitizeWeight(brandFonts.small_weight, 400)} !important;
      }
    `
    : "";

  // Update a per-platform recommendation in the edits buffer + locally in the
  // view payload so the user sees the change immediately.
  const onPlatformRecEdit = (buId, platform, newText) => {
    setEdits((prev) => ({
      ...prev,
      platform_recommendations: {
        ...prev.platform_recommendations,
        [buId]: { ...(prev.platform_recommendations[buId] || {}), [platform]: newText },
      },
    }));
    setReport((prev) => {
      if (!prev) return prev;
      const vd = { ...(prev.view_data || {}) };
      vd.property_blocks = (vd.property_blocks || []).map((blk) => {
        if (blk.business_unit_id !== buId) return blk;
        return {
          ...blk,
          platform_summaries: (blk.platform_summaries || blk.platforms || []).map((p) =>
            p.platform === platform ? { ...p, recommendation: newText } : p),
        };
      });
      return { ...prev, view_data: vd };
    });
  };

  const setSummary = (v) => {
    setEdits((e) => ({ ...e, summary_md: v }));
    setReport((p) => ({ ...p, summary_md: v }));
  };
  const setNextMonthFocus = (v) => {
    setEdits((e) => ({ ...e, next_month_focus_md: v }));
    setReport((p) => ({ ...p, next_month_focus_md: v }));
  };
  const setAnecdotal = (v) => {
    setEdits((e) => ({ ...e, anecdotal_notes: v }));
    setReport((p) => ({ ...p, view_data: { ...(p.view_data || {}), anecdotal_notes: v } }));
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (edits.summary_md !== null) payload.summary_md = edits.summary_md;
      if (edits.next_month_focus_md !== null) payload.next_month_focus_md = edits.next_month_focus_md;
      if (edits.anecdotal_notes !== null) payload.anecdotal_notes = edits.anecdotal_notes;
      if (Object.keys(edits.platform_recommendations).length > 0) {
        payload.platform_recommendations = edits.platform_recommendations;
      }
      if (edits.social_posts_excluded_ids !== null) {
        payload.social_posts_excluded_ids = edits.social_posts_excluded_ids;
      }
      if (edits.social_posts_caption_overrides !== null) {
        payload.social_posts_caption_overrides = edits.social_posts_caption_overrides;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setEditing(false);
        return;
      }
      await api.patch(`/reports/${reportId}`, payload);
      toast.success("Edits saved");
      setEditing(false);
      setEdits({
        summary_md: null, next_month_focus_md: null,
        anecdotal_notes: null, platform_recommendations: {},
        social_posts_excluded_ids: null, social_posts_caption_overrides: null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const cancelEditing = () => {
    setEditing(false);
    setEdits({
      summary_md: null, next_month_focus_md: null,
      anecdotal_notes: null, platform_recommendations: {},
      social_posts_excluded_ids: null, social_posts_caption_overrides: null,
    });
    // Reload to discard local edits
    api.get(`/reports/${reportId}/view`).then((r) => setReport(r.data)).catch(() => {});
  };

  const createPdf = async () => {
    setCreatingPdf(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/create-pdf`);
      toast.success(data.report?.status === "finalized" ? "PDF created" : "PDF created");
      setReport((p) => ({ ...p, ...(data.report || {}) }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF creation failed");
    } finally { setCreatingPdf(false); }
  };

  const emailReport = async () => {
    const confirmMsg = report?.last_emailed_at
      ? `Re-send this PDF to all client contacts?\n\nLast sent at ${report.last_emailed_at.slice(0,16).replace("T", " ")} UTC.`
      : "Send this finalized PDF to all client contacts now?";
    if (!window.confirm(confirmMsg)) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/email`);
      if (data?.sent) {
        const n = (data.recipients || []).length;
        const cc = (data.cc || []).length;
        toast.success(
          `Sent to ${n} contact${n === 1 ? "" : "s"}` +
          (cc ? ` (+${cc} cc'd)` : "") +
          (data.source ? ` via ${data.source}` : ""));
        setReport((p) => ({ ...p,
          last_emailed_at: new Date().toISOString(),
          last_emailed_to: data.recipients }));
      } else {
        toast.error(data?.reason || "Send failed (no error returned)");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email send failed");
    } finally { setEmailing(false); }
  };

  const refreshReport = async () => {
    if (!window.confirm(
      "Refresh this report?\n\n" +
      "This will re-pull every CSV upload and worklog for " +
      (report?.period_month || "this month") +
      " into the report. Your edits to the executive summary, anecdotal notes, " +
      "and AI recommendations are preserved.\n\n" +
      (report?.pdf_path
        ? "Tip: re-click 'Create PDF' afterwards to regenerate the PDF with the fresh data."
        : "")
    )) return;
    setRefreshing(true);
    try {
      await api.post(`/reports/${reportId}/refresh`);
      toast.success("Report refreshed with latest data");
      // Reload the view payload
      const { data } = await api.get(`/reports/${reportId}/view`);
      setReport(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading report…
      </div>
    );
  }
  if (!report) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600">Report not found.</p>
        <Link to={`/clients/${clientId}/reports`} className="text-xs label-overline text-[#002FA7] hover:underline mt-3 inline-block">
          ← Back to reports
        </Link>
      </div>
    );
  }

  const downloadPdf = () => {
    fetch(`${BACKEND}/api/reports/${reportId}/pdf`, {
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${report.company_name}_${report.period_month}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="view-report-page"
      data-brand-report={reportId}>
      {brandFontsCss && (
        /* CSS values are pre-sanitized above (font family regex-stripped,
           weights integer-clamped 100-900). We pass the sanitized string
           as children of the <style> tag — React inserts it as the
           element's text content, NOT as parsed HTML, so no script
           execution is possible. This is the React-safe equivalent of
           inline style strings. */
        <style>{brandFontsCss}</style>
      )}
      {/* Sticky page actions — hidden when printing */}
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <Button variant="ghost" className="rounded-sm" onClick={() => navigate(-1)}
          data-testid="view-report-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status pill */}
          <Badge className={`rounded-sm ${report.status === "finalized" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}
            data-testid="view-report-status">
            {report.status === "finalized" ? "PDF created" : "Draft — PDF not created"}
          </Badge>

          {isAdmin && !editing && (
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(true)}
              data-testid="view-report-edit">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
          {isAdmin && editing && (
            <>
              <Button variant="ghost" className="rounded-sm" onClick={cancelEditing} disabled={saving}
                data-testid="view-report-cancel-edit">
                Cancel
              </Button>
              <Button className="rounded-sm bg-zinc-900 hover:bg-black text-white" onClick={saveEdits} disabled={saving}
                data-testid="view-report-save-edit">
                <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save edits"}
              </Button>
            </>
          )}

          <Button variant="outline" className="rounded-sm" onClick={() => window.print()}
            data-testid="view-report-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
          </Button>

          {report.pdf_path && (
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              onClick={downloadPdf} data-testid="view-report-download-pdf">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
            </Button>
          )}

          {isAdmin && (
            <Button variant="outline" className="rounded-sm border-zinc-300"
              onClick={refreshReport} disabled={refreshing || editing}
              data-testid="view-report-refresh"
              title="Re-pull every CSV upload + worklog for this month into the report. Preserves your text edits.">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh report"}
            </Button>
          )}

          {isAdmin && (
            <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={createPdf} disabled={creatingPdf || editing}
              data-testid="view-report-create-pdf">
              <FileCheck className="h-3.5 w-3.5 mr-1.5" />
              {creatingPdf ? "Creating…" : (report.pdf_path ? "Re-create PDF" : "Create PDF")}
            </Button>
          )}

          {isAdmin && report.status === "finalized" && (
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
              onClick={emailReport} disabled={emailing || editing}
              data-testid="view-report-email"
              title={report.last_emailed_at
                ? `Last emailed ${(report.last_emailed_at || '').slice(0,16).replace('T',' ')}`
                : "Send the finalized PDF to all client contacts"}>
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {emailing ? "Sending…" :
                (report.last_emailed_at ? "Resend email" : "Email to client")}
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-2 text-xs text-amber-800 print:hidden">
          <strong>Edit mode.</strong> Update the executive summary, anecdotal notes, and AI
          recommendations below. Click <strong>Save edits</strong> when done, then{" "}
          <strong>Create PDF</strong> to finalize.
        </div>
      )}

      {/* ===== COVER PAGE ===== */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden" data-testid="view-report-cover">
        <CardContent className="p-10">
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start">
            <div>
              <div className="label-overline">Iceberg Digital · Monthly Report</div>
              <h1 className="font-display font-extrabold tracking-tight mt-2 leading-none"
                style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)" }}
                data-testid="view-report-headline">
                Marketing Report for{" "}
                <span style={{ color: primary }}>{formatPeriodLong(report.period_month)}</span>
              </h1>
              <div className="mt-3 text-lg text-zinc-700 font-display font-bold">
                {report.company_name}
              </div>
              {report.generated_at && (
                <div className="mt-1 text-xs text-zinc-500 font-mono">
                  Generated {formatAuDateTime(report.generated_at)}
                </div>
              )}

              {/* Contacts */}
              {report.contacts?.length > 0 && (
                <div className="mt-8">
                  <div className="label-overline">Your team at {report.company_name}</div>
                  <div className="mt-2 space-y-1.5">
                    {report.contacts.map((c) => (
                      <ContactLine key={c.user_id} c={c} />
                    ))}
                  </div>
                </div>
              )}

              {/* Agency reps */}
              {report.agency_reps?.length > 0 && (
                <div className="mt-4">
                  <div className="label-overline">Your Iceberg Digital team</div>
                  <div className="mt-2 space-y-1.5">
                    {report.agency_reps.map((r) => (
                      <ContactLine key={r.rep_id} c={r} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            {report.logo_url && (
              <img src={`${BACKEND}${report.logo_url}`} alt={report.company_name}
                className="max-h-40 max-w-[220px] object-contain"
                data-testid="view-report-logo" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* ===== EXECUTIVE SUMMARY ===== */}
      {(report.summary_md || editing) && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4" style={{ color: primary }} />
              <div className="label-overline">Executive summary</div>
            </div>
            {editing ? (
              <textarea value={report.summary_md || ""}
                onChange={(e) => setSummary(e.target.value)}
                className="w-full text-sm leading-6 text-zinc-800 font-sans border border-zinc-300 rounded-sm p-3"
                rows={Math.max(6, Math.min(16, (report.summary_md || "").split("\n").length + 2))}
                data-testid="edit-summary-md" />
            ) : (
              <pre className="whitespace-pre-wrap text-sm leading-6 text-zinc-800 font-sans"
                data-testid="view-report-summary">
                {report.summary_md}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== NEXT MONTH'S FOCUS ===== */}
      {(report.next_month_focus_md || editing) && (
        <Card className="rounded-sm border-zinc-200"
          style={{ borderLeftWidth: 4, borderLeftColor: primary }}
          data-testid="view-report-next-month-focus-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4" style={{ color: primary }} />
              <div className="label-overline">Next month&apos;s focus</div>
            </div>
            {editing ? (
              <>
                <textarea value={report.next_month_focus_md || ""}
                  onChange={(e) => setNextMonthFocus(e.target.value)}
                  placeholder={"• Action 1 — tie to a specific metric\n• Action 2 — expected impact\n• Action 3 …"}
                  className="w-full text-sm leading-6 text-zinc-800 font-sans border border-zinc-300 rounded-sm p-3"
                  rows={Math.max(5, Math.min(12,
                    (report.next_month_focus_md || "").split("\n").length + 2))}
                  data-testid="edit-next-month-focus-md" />
                <div className="mt-2 text-[11px] text-zinc-500">
                  Tip: start each action on a new line with <code>• </code>.
                </div>
              </>
            ) : (
              <ul className="space-y-1.5 text-sm leading-6 text-zinc-800 font-sans"
                data-testid="view-report-next-month-focus">
                {(report.next_month_focus_md || "")
                  .split("\n")
                  .map((ln) => ln.trim().replace(/^[•*\-·]\s*/, "").trim())
                  .filter(Boolean)
                  .map((ln, i) => (
                    <li key={i} className="flex gap-2"
                      data-testid={`view-report-next-month-focus-item-${i}`}>
                      <span style={{ color: primary }}>•</span>
                      <span>{ln}</span>
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== ACTIVITY NOTES (under exec summary) ===== */}
      {(report.view_data?.anecdotal_notes || editing) && (
        <Card className="rounded-sm border-zinc-200" data-testid="view-report-activity-notes">
          <CardContent className="p-6 space-y-2">
            <div className="label-overline">Activity notes</div>
            {editing ? (
              <textarea
                value={report.view_data?.anecdotal_notes || ""}
                onChange={(e) => setAnecdotal(e.target.value)}
                rows={4}
                className="w-full text-sm leading-6 text-zinc-800 font-sans border border-zinc-300 rounded-sm p-3"
                placeholder="Anecdotal context (e.g. 'During January we built 5 new case-study pages')…"
                data-testid="edit-anecdotal-notes" />
            ) : (
              <p className="text-sm text-zinc-700 whitespace-pre-wrap"
                data-testid="view-report-anecdotal">
                {report.view_data.anecdotal_notes}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* On overarching reports we ONLY want the by-service view —
          hide the per-property Live-sources + Properties-summary
          panels so the reader focuses on cross-property comparison.
          Guard: an empty {} services_grouped is truthy in JS so we
          check the key count, not the value itself. */}
      {!(report.view_data?.services_grouped
         && Object.keys(report.view_data.services_grouped).length > 0) && (
        <>
          {/* ===== LIVE DATA SOURCES (property-scoped reports only) ===== */}
          <LiveSourcesPanel sources={report.view_data?.live_sources} primary={primary} />
        </>
      )}

      {/* ===== SERVICES GROUPED (overarching reports only) — one section
              per service (GA4, Search Console, Ads, YouTube, Meta,
              Microsoft), each with a "Total across all properties"
              headline card plus a per-property breakdown grid. */}
      <ServicesGroupedPanel
        servicesGrouped={report.view_data?.services_grouped}
        primary={primary} />

      {/* ===== PAGE 3 — PROPERTIES SUMMARY + TREND CHARTS ===== */}
      {!(!report.business_unit_id
         && report.view_data?.services_grouped
         && Object.keys(report.view_data.services_grouped).length > 0) && (
        <PropertiesSummaryTable report={report} primary={primary} />
      )}
      <TrendCharts trends={report.view_data?.monthly_trends} primary={primary} alwaysShow />

      {/* ===== PER-PROPERTY CHANNEL DETAILS =====
       * Hidden ONLY on OVERARCHING reports where the by-service panel
       * already covers everything (those old per-property Budget /
       * Spend / Leads / CPC / CPCV rows would duplicate what
       * ServicesGroupedPanel shows). Scoped (single-property) reports
       * always show property_blocks because that's where per-property
       * Google Ads / GA4 / YouTube / Meta / GSC data lives — the
       * scoped-report code path deliberately keeps services_grouped
       * empty for those channels and instead ships them under
       * property_blocks.platform_summaries.
       */}
      {!(!report.business_unit_id
         && report.view_data?.services_grouped
         && Object.keys(report.view_data.services_grouped).length > 0)
        && (report.view_data?.property_blocks || []).map((block) => (
          <PropertyBlock key={block.business_unit_id} block={block} primary={primary}
            editing={editing}
            onPlatformRecEdit={isAdmin ? onPlatformRecEdit : null} />
        ))}

      {/* ===== WORKLOG HOURS REPORT ===== */}
      <WorklogReport summary={report.worklog_summary} rows={report.worklog_rows} primary={primary} />

      {/* ===== SOCIAL POSTS (Facebook + Instagram organic, per-client) ===== */}
      <SocialPostsSection
        socialPosts={report.view_data?.social_posts}
        edits={report.view_data?.social_posts_edits}
        primary={primary}
        editing={editing && isAdmin}
        onTogglePost={(postId, excluded) => {
          setEdits((prev) => {
            const cur = new Set(
              prev.social_posts_excluded_ids
              ?? ((report.view_data?.social_posts_edits || {}).excluded_ids || []),
            );
            if (excluded) cur.add(postId); else cur.delete(postId);
            return { ...prev, social_posts_excluded_ids: Array.from(cur) };
          });
          // Mirror in the local report so the UI updates immediately.
          setReport((p) => {
            const vd = { ...(p.view_data || {}) };
            const spe = { ...(vd.social_posts_edits || {}) };
            const cur = new Set(spe.excluded_ids || []);
            if (excluded) cur.add(postId); else cur.delete(postId);
            spe.excluded_ids = Array.from(cur);
            vd.social_posts_edits = spe;
            return { ...p, view_data: vd };
          });
        }}
        onCaptionOverride={(postId, text) => {
          setEdits((prev) => {
            const cur = {
              ...(prev.social_posts_caption_overrides
                  ?? ((report.view_data?.social_posts_edits || {}).caption_overrides || {})),
            };
            if (text === null || text === undefined) delete cur[postId];
            else cur[postId] = text;
            return { ...prev, social_posts_caption_overrides: cur };
          });
          setReport((p) => {
            const vd = { ...(p.view_data || {}) };
            const spe = { ...(vd.social_posts_edits || {}) };
            const cur = { ...(spe.caption_overrides || {}) };
            if (text === null || text === undefined) delete cur[postId];
            else cur[postId] = text;
            spe.caption_overrides = cur;
            vd.social_posts_edits = spe;
            return { ...p, view_data: vd };
          });
        }}
      />

      {/* ===== BUDGET TOTAL vs ACTUAL COST ===== */}
      <BudgetTotals report={report} primary={primary} />

      {/* ===== FINAL PAGE — TOTAL COSTS & ROI SUMMARY ===== */}
      <RoiSummary report={report} primary={primary} />
    </div>
  );
}

function ContactLine({ c }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const avatar = c.avatar_url
    ? (c.avatar_url.startsWith("http") ? c.avatar_url : `${BACKEND_URL}${c.avatar_url}`)
    : null;
  const name = c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
  const phone = c.phone || c.mobile_phone;
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="h-7 w-7 rounded-full overflow-hidden bg-zinc-100 border border-zinc-200 flex-shrink-0 flex items-center justify-center text-[10px] text-zinc-500">
        {avatar ? <img src={avatar} alt={name} className="w-full h-full object-cover" /> : <Users className="h-3.5 w-3.5" />}
      </div>
      <span className="font-display font-bold">{name}</span>
      {c.title && <span className="text-xs text-zinc-500">· {c.title}</span>}
      {c.email && (
        <a href={`mailto:${c.email}`} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
          <Mail className="h-3 w-3" /> {c.email}
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
          <Phone className="h-3 w-3" /> {phone}
        </a>
      )}
    </div>
  );
}

/**
 * LiveSourcesPanel — surfaces the result of attempting a LIVE fetch on every
 * api_connection in scope for a property-scoped report. The backend
 * `_collect_live_sources_for_scope` returns one row per connection with
 * status in {ok, empty, error, not_implemented}; we render each as a
 * compact card so the admin can see in one glance:
 *   - which platforms returned live data
 *   - which platforms surfaced an error AND how to fix it
 *   - which platforms are still only available via CSV upload
 * Returns null on overarching reports (no live_sources collected) so the
 * existing UI shape is unchanged for those.
 */
function LiveSourcesPanel({ sources, primary }) {
  if (!sources || sources.length === 0) return null;

  const okRows = sources.filter((s) => s.status === "ok");
  const emptyRows = sources.filter((s) => s.status === "empty");
  const errorRows = sources.filter((s) => s.status === "error");
  const todoRows = sources.filter((s) => s.status === "not_implemented");

  return (
    <Card className="rounded-sm" data-testid="live-sources-panel">
      <CardContent className="p-5 space-y-4">
        <div>
          <div className="label-overline" style={{ color: primary }}>
            Live data sources for this property
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Pulled from the API connections marked <strong>connected</strong> on this
            property. Platforms not connected are silently skipped — connect them on
            the API Connections tab to add them here.
          </p>
        </div>

        {!!okRows.length && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase font-bold tracking-wider text-emerald-700">
              ✓ Live data ({okRows.length})
            </div>
            {okRows.map((s, i) => (
              <LiveSourceCard key={`${s.platform}-${i}`} source={s} variant="ok" />
            ))}
          </div>
        )}

        {!!emptyRows.length && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase font-bold tracking-wider text-zinc-500">
              ○ Connected but no data ({emptyRows.length})
            </div>
            {emptyRows.map((s, i) => (
              <LiveSourceCard key={`${s.platform}-${i}`} source={s} variant="empty" />
            ))}
          </div>
        )}

        {!!errorRows.length && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase font-bold tracking-wider text-red-700"
              data-testid="live-sources-errors-header">
              ⚠ Connection errors ({errorRows.length})
            </div>
            {errorRows.map((s, i) => (
              <LiveSourceCard key={`${s.platform}-${i}`} source={s} variant="error" />
            ))}
          </div>
        )}

        {!!todoRows.length && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase font-bold tracking-wider text-zinc-500">
              ○ CSV upload only (live API not yet wired) — {todoRows.length}
            </div>
            {todoRows.map((s, i) => (
              <LiveSourceCard key={`${s.platform}-${i}`} source={s} variant="todo" />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LiveSourceCard({ source, variant }) {
  const styles = {
    ok:    { bg: "bg-emerald-50", border: "border-emerald-200", title: "text-emerald-900" },
    empty: { bg: "bg-zinc-50",    border: "border-zinc-200",    title: "text-zinc-700" },
    error: { bg: "bg-red-50",     border: "border-red-200",     title: "text-red-900" },
    todo:  { bg: "bg-amber-50",   border: "border-amber-200",   title: "text-amber-900" },
  }[variant] || { bg: "bg-zinc-50", border: "border-zinc-200", title: "text-zinc-700" };
  const totals = source?.data?.totals || {};
  const totalsKeys = Object.keys(totals).slice(0, 6);
  return (
    <div className={`rounded-sm border ${styles.border} ${styles.bg} p-3`}
      data-testid={`live-source-${source.platform}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`font-bold text-sm ${styles.title}`}>{source.label}</span>
        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
          {source.scope}
        </span>
        {source.account_label && (
          <span className="text-[10px] font-mono text-zinc-500">· {source.account_label}</span>
        )}
        {source.resource_label && (
          <span className="text-[10px] font-mono text-zinc-500">· {source.resource_label}</span>
        )}
      </div>
      {variant === "ok" && !!totalsKeys.length && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
          {totalsKeys.map((k) => (
            <div key={k} className="bg-white rounded-sm border border-emerald-100 px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{_labelFor(k)}</div>
              <div className="font-display font-bold text-sm">
                {_fmtMetric(k, totals[k])}
              </div>
            </div>
          ))}
        </div>
      )}
      {variant === "ok" && source.platform === "ahrefs" && (
        <AhrefsBreakdowns breakdowns={source?.data?.breakdowns}
          testIdPrefix={`live-source-${source.platform}`} />
      )}
      {variant === "ok" && source.platform === "meta" && (
        <MetaBreakdowns breakdowns={source?.data?.breakdowns}
          testIdPrefix={`live-source-${source.platform}`} />
      )}
      {variant === "empty" && (
        <div className="text-[11px] text-zinc-500 mt-1">
          The API returned no totals or breakdowns for{" "}
          {source.start_date}…{source.end_date}.
        </div>
      )}
      {(variant === "error" || variant === "todo") && (
        <div className="space-y-1.5 mt-2">
          <div className="text-[11px] font-mono text-red-700"
            data-testid={`live-source-error-${source.platform}`}>
            {source.error}
          </div>
          <div className="text-[11px] text-zinc-700 leading-relaxed">
            <strong>How to fix:</strong> {source.fix_hint}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
 * ServicesGroupedPanel — overarching (all-properties) reports only.
 *
 * Renders one Card per service. Each Card shows:
 *   ① Headline: the sum of every SUM-safe metric across every
 *     property that returned data for this service.
 *   ② Per-property breakdown grid — one row per BU with its
 *     status pill, resource label, and the same metric list, plus
 *     an error/fix-hint block when a property couldn't fetch.
 * Properties without the service assigned are omitted from the
 * grid (backend already filters them so the row list is honest).
 * ─────────────────────────────────────────────────────────────── */
// Preferred order for the "Marketing performance — by service" panel.
// GA4 comes first because it's the traffic + conversions source-of-truth
// that anchors the whole report narrative; every other channel's value
// is easier to interpret once you know overall sessions/engagement.
// Any service key not listed here falls to the end in alphabetical order.
const _SERVICE_ORDER = [
  "google_analytics",       // GA4 — sessions, users, engagement
  "google_search_console",  // Organic search performance
  "google_ads",             // Paid Google
  "microsoft_ads",          // Paid Bing (legacy key)
  "meta",                   // Facebook + Instagram (organic + paid)
  "youtube",                // YouTube channel performance
  "ahrefs",                 // SEO / backlink metrics
  "semrush",                // SEO — second independent authority
  // ── Additional "sellable" placeholders — always shown so clients
  // ── see the marketing channels available on our platform.
  "openai_ads",
  "microsoft",
  "microsoft_bing",
  "linkedin",
  "twitter",
  "tiktok",
  "pinterest",
  "reviews",
  "sendgrid",
  "email_campaigns",
  "sms_rcs",
  "twilio",
  "call_centre",
  "leads",
  "worklogs",
];

function ServicesGroupedPanel({ servicesGrouped, primary }) {
  const rawKeys = servicesGrouped ? Object.keys(servicesGrouped) : [];
  if (!rawKeys.length) return null;
  // Sort keys by _SERVICE_ORDER first, then alphabetical for anything
  // not in the preference list. Missing keys still render — just at
  // the tail of the list.
  const keys = [...rawKeys].sort((a, b) => {
    const ia = _SERVICE_ORDER.indexOf(a);
    const ib = _SERVICE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return (
    <div className="space-y-4" data-testid="services-grouped-panel">
      <Card className="rounded-sm border-zinc-200 bg-zinc-50/40">
        <CardContent className="p-5">
          <div className="label-overline" style={{ color: primary }}>
            Marketing performance — by service
          </div>
          <p className="text-xs text-zinc-500 mt-1 max-w-3xl">
            All live-fetched metrics for the reporting period, grouped by
            service and then broken down by property. Aggregate totals
            sum every SUM-safe metric across every property that returned
            data for that service; per-property rows show the raw
            numbers for that individual assignment.
          </p>
        </CardContent>
      </Card>
      {keys.map((key) => (
        <ServiceSection key={key} serviceKey={key}
          data={servicesGrouped[key]} primary={primary} />
      ))}
    </div>
  );
}

const _METRIC_UI = {
  // Numeric formatters — how each key is displayed in the metric grid.
  // Keys are UI keys (snake_case, matching what backend collectors emit).
  cost: {label: "Cost", prefix: "$", digits: 2},
  cost_micros: {label: "Cost (micros)", digits: 0},
  clicks: {label: "Clicks"}, impressions: {label: "Impressions"},
  ctr: {label: "CTR", suffix: "%", digits: 2},
  conversions: {label: "Conversions", digits: 0},
  conversions_value: {label: "Conv. value", prefix: "$", digits: 2},
  sessions: {label: "Sessions"}, total_users: {label: "Total users"},
  new_users: {label: "New users"}, active_users: {label: "Active users"},
  engagement_rate: {label: "Engagement rate", suffix: "%", digits: 1, x100: true},
  screen_page_views: {label: "Page views"},
  page_followers: {label: "Followers"}, page_likes: {label: "Page likes"},
  page_impressions: {label: "Page impressions"},
  page_impressions_paid: {label: "Paid impressions"},
  page_impressions_organic: {label: "Organic impressions"},
  page_reach: {label: "Page reach"},
  page_post_engagements: {label: "Post engagements"},
  page_video_views: {label: "Page video views"},
  page_video_views_unique: {label: "Unique video viewers"},
  page_views_total: {label: "Page views"},
  page_total_actions: {label: "Page CTAs"},
  page_engaged_users: {label: "Engaged users"},
  reach: {label: "Reach"}, engagement: {label: "Engagement"},
  video_views: {label: "Video views"},
  ig_followers: {label: "IG followers"}, ig_posts: {label: "IG posts"},
  ig_reach: {label: "IG reach"}, ig_impressions: {label: "IG impressions"},
  ig_profile_views: {label: "IG profile views"},
  views: {label: "Views"},
  watch_time_minutes: {label: "Watch mins", digits: 1},
  subscribers_gained: {label: "Subs gained"}, subscribers_lost: {label: "Subs lost"},
  likes: {label: "Likes"}, dislikes: {label: "Dislikes"},
  comments: {label: "Comments"}, shares: {label: "Shares"},
  position: {label: "Avg. position", digits: 1},
  // AHREFS SEO metrics
  domain_rating: {label: "Domain Rating", digits: 1},
  url_rating: {label: "URL Rating", digits: 1},
  backlinks: {label: "Backlinks", digits: 0},
  refdomains: {label: "Referring domains", digits: 0},
  org_traffic: {label: "Organic traffic", digits: 0},
  org_traffic_value: {label: "Organic traffic value", prefix: "$", digits: 0},
  org_keywords: {label: "Organic keywords", digits: 0},
  paid_traffic: {label: "Paid traffic", digits: 0},
  paid_traffic_value: {label: "Paid traffic value", prefix: "$", digits: 0},
  paid_keywords: {label: "Paid keywords", digits: 0},
  backlinks_sample: {label: "Backlinks sampled", digits: 0},
  dofollow_sample: {label: "Dofollow sampled", digits: 0},
  organic_keywords_sample: {label: "Keywords sampled", digits: 0},
  organic_keywords_traffic_sample: {label: "Sampled kw traffic", digits: 0},
  top_pages_sample: {label: "Top pages sampled", digits: 0},
  referring_domains_sample: {label: "Ref domains sampled", digits: 0},
  // SendGrid Email
  requests: {label: "Requests", digits: 0},
  delivered: {label: "Delivered", digits: 0},
  opens: {label: "Opens", digits: 0},
  unique_opens: {label: "Unique opens", digits: 0},
  unique_clicks: {label: "Unique clicks", digits: 0},
  bounces: {label: "Bounces", digits: 0},
  blocks: {label: "Blocks", digits: 0},
  spam_reports: {label: "Spam reports", digits: 0},
  unsubscribes: {label: "Unsubscribes", digits: 0},
  invalid_emails: {label: "Invalid emails", digits: 0},
  deferred: {label: "Deferred", digits: 0},
  delivery_rate: {label: "Delivery rate", suffix: "%", digits: 2},
  open_rate: {label: "Open rate", suffix: "%", digits: 2},
  click_rate: {label: "Click rate", suffix: "%", digits: 2},
};

function _fmtMetric(k, v) {
  if (v === null || v === undefined || v === "") return "—";
  const cfg = _METRIC_UI[k] || {};
  let n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  if (cfg.x100) n = n * 100;
  // Smart fallback for any metric key ending in `_rate` — treat as a
  // percentage with 2 decimal places even when no explicit config exists.
  const isRateKey = !_METRIC_UI[k] && /(^|_)rate$/.test(k || "");
  const digits = cfg.digits ?? (isRateKey ? 2
    : (Math.abs(n) < 10 && n !== 0 ? 2 : 0));
  const body = n.toLocaleString(undefined,
    {minimumFractionDigits: digits, maximumFractionDigits: digits});
  const suffix = cfg.suffix ?? (isRateKey ? "%" : "");
  return `${cfg.prefix || ""}${body}${suffix}`;
}

function _labelFor(k) {
  return (_METRIC_UI[k] || {}).label
    || k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function ServiceSection({ serviceKey, data, primary }) {
  const totals = data?.total?.totals || {};
  const totalKeys = Object.keys(totals);
  const props = data?.by_property || [];
  const ss = data?.status_summary || {};
  const anyOk = (ss.ok || 0) > 0;
  const anyErr = (ss.error || 0) > 0;

  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden"
      data-testid={`service-section-${serviceKey}`}>
      <div className="px-5 py-3 border-b border-zinc-200 bg-white
        flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-lg leading-tight"
            style={{ color: primary }}>{data?.label || serviceKey}</div>
          <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
            {props.length} propert{props.length === 1 ? "y" : "ies"} ·
            {" "}{ss.ok || 0} OK · {ss.empty || 0} empty ·
            {" "}{ss.error || 0} error · {ss.not_implemented || 0} pending
          </div>
        </div>
        {anyOk && !!totalKeys.length && (
          <Badge className="rounded-sm font-mono text-[10px]"
            style={{ backgroundColor: `${primary}15`, color: primary,
                     borderColor: `${primary}40` }}>
            Data from {data.total.by_property_count} propert
            {data.total.by_property_count === 1 ? "y" : "ies"}
          </Badge>
        )}
      </div>
      <CardContent className="p-5 space-y-4">
        {data?.is_placeholder && (
          <div className="rounded-sm border border-amber-300 bg-amber-50
            px-3 py-2 text-[11px] text-amber-900 leading-relaxed
            italic"
            data-testid={`service-placeholder-${serviceKey}`}>
            <strong>Not yet connected.</strong> This section is included so
            you can see the marketing data available once we wire this
            channel into your reporting. Ask your account manager to enable
            it.
          </div>
        )}
        {/* Aggregate totals card */}
        {anyOk && !!totalKeys.length && (
          <div>
            <div className="label-overline mb-2">
              Total across all properties
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {totalKeys.map((k) => (
                <div key={k}
                  className="rounded-sm border border-zinc-200 bg-white
                    px-2.5 py-2"
                  data-testid={`service-total-${serviceKey}-${k}`}>
                  <div className="text-[9px] uppercase tracking-wider
                    text-zinc-500 leading-tight">{_labelFor(k)}</div>
                  <div className="font-display font-bold text-base mt-0.5"
                    style={{ color: primary }}>
                    {_fmtMetric(k, totals[k])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-property breakdown */}
        <div>
          <div className="label-overline mb-2">
            By property
          </div>
          <div className="space-y-2">
            {props.map((p, i) => (
              <PropertyMetricsRow
                key={`${serviceKey}-${p.business_unit_id || `ph-${i}`}`}
                p={p} primary={primary} serviceKey={serviceKey} />
            ))}
          </div>
        </div>

        {/* Contextual helper for services with 100% error rate */}
        {!anyOk && anyErr && (
          <div className="text-[11px] text-red-700 bg-red-50 border
            border-red-200 rounded-sm p-2.5 leading-relaxed">
            <strong>No properties returned live data for this service.</strong>
            {" "}Fix the individual property errors below — most are
            one-click Cloud Console API enables or account re-invitations.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PropertyMetricsRow({ p, primary, serviceKey }) {
  const statusStyle = {
    ok:    { bg: "bg-emerald-50", border: "border-emerald-200",
             label: "OK", labelBg: "bg-emerald-600" },
    empty: { bg: "bg-zinc-50",    border: "border-zinc-200",
             label: "EMPTY", labelBg: "bg-zinc-500" },
    error: { bg: "bg-red-50",     border: "border-red-200",
             label: "ERROR", labelBg: "bg-red-600" },
    not_implemented: { bg: "bg-amber-50", border: "border-amber-200",
             label: "PENDING", labelBg: "bg-amber-600" },
  }[p.status] || { bg: "bg-zinc-50", border: "border-zinc-200",
                    label: (p.status || "").toUpperCase(),
                    labelBg: "bg-zinc-500" };
  const totalKeys = Object.keys(p.totals || {});

  return (
    <div className={`rounded-sm border ${statusStyle.border} ${statusStyle.bg}
      p-3`}
      data-testid={`property-metrics-${serviceKey}-${p.business_unit_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-white text-[9px] font-mono uppercase
          tracking-wider ${statusStyle.labelBg} px-1.5 py-0.5 rounded-sm`}>
          {statusStyle.label}
        </span>
        <span className="font-display font-bold text-sm">
          {p.business_unit_name}
        </span>
        {p.resource_label && (
          <span className="text-[10px] font-mono text-zinc-500 truncate max-w-[300px]">
            · {p.resource_label}
          </span>
        )}
        {p.resource_id && !p.resource_label && (
          <span className="text-[10px] font-mono text-zinc-500 truncate max-w-[300px]">
            · {p.resource_id}
          </span>
        )}
        {p.via_google_email && (
          <span className="text-[9px] font-mono text-zinc-400 ml-auto">
            fetched via {p.via_google_email}
          </span>
        )}
      </div>

      {p.status === "ok" && !!totalKeys.length && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
          {totalKeys.map((k) => (
            <div key={k}
              className="rounded-sm bg-white border border-emerald-100
                px-2 py-1">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500">
                {_labelFor(k)}
              </div>
              <div className="font-display font-bold text-[13px]"
                style={{ color: primary }}>
                {_fmtMetric(k, p.totals[k])}
              </div>
            </div>
          ))}
        </div>
      )}

      {p.status === "ok" && serviceKey === "ahrefs" && (
        <AhrefsBreakdowns breakdowns={p.breakdowns}
          testIdPrefix={`property-metrics-${serviceKey}-${p.business_unit_id}`} />
      )}
      {p.status === "ok" && serviceKey === "meta" && (
        <MetaBreakdowns breakdowns={p.breakdowns}
          testIdPrefix={`property-metrics-${serviceKey}-${p.business_unit_id}`} />
      )}

      {p.status === "empty" && (
        <div className="text-[11px] text-zinc-500 mt-1">
          The API returned no totals for this property in this period.
        </div>
      )}

      {p.status === "error" && (
        <div className="mt-1.5 space-y-1">
          <div className="text-[11px] font-mono text-red-700 leading-snug">
            {p.error}
          </div>
          <div className="text-[11px] text-zinc-700 leading-relaxed">
            <strong>Fix:</strong> {p.fix_hint}
          </div>
          {p.enable_api_url && (
            <a href={p.enable_api_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px]
                font-bold px-2 py-1 rounded-sm bg-amber-600 text-white
                hover:bg-amber-700 mt-1"
              data-testid={`enable-api-btn-${serviceKey}-${p.business_unit_id}`}>
              Enable this API in Google Cloud →
            </a>
          )}
        </div>
      )}

      {p.status === "not_implemented" && (
        <div className="text-[11px] text-zinc-600 mt-1 leading-relaxed">
          {p.fix_hint || "Live-API fetch not yet wired for this platform. " +
            "Upload the monthly CSV/Excel on the Data page for now."}
        </div>
      )}
    </div>
  );
}


/* AhrefsBreakdowns extracted to a shared component
 * (see /components/AhrefsBreakdowns.js) so the per-property
 * Feeds page can render the same tables from live-preview data
 * without going through the report pipeline. */



function PropertiesSummaryTable({ report, primary }) {
  const blocks = report.view_data?.property_blocks || [];
  if (blocks.length === 0) return null;
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6">
        <div className="label-overline mb-1">Properties summary</div>
        <p className="text-xs text-zinc-500 mb-3">
          Budget vs actual spend, plus website visits change vs previous month.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="view-report-properties-table">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="text-left py-2 pr-3">Property</th>
                <th className="text-right py-2 px-3">Budget</th>
                <th className="text-right py-2 px-3">Actual</th>
                <th className="text-right py-2 px-3">Visits</th>
                <th className="text-right py-2 pl-3">Δ vs prev</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => {
                const delta = b.visits_delta_pct;
                return (
                  <tr key={b.business_unit_id} className="border-b border-zinc-100"
                    data-testid={`view-property-row-${b.business_unit_id}`}>
                    <td className="py-2 pr-3 font-semibold">{b.name}</td>
                    <td className="py-2 px-3 text-right font-mono">
                      {b.budget_total > 0 ? `$${b.budget_total.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {b.actual_total > 0 ? `$${b.actual_total.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{fmtBig(b.current_visits || 0)}</td>
                    <td className="py-2 pl-3 text-right font-mono">
                      {delta == null ? "—" : (
                        <span style={{ color: delta >= 0 ? "#16a34a" : "#dc2626" }}>
                          {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function TrendCharts({ trends, primary, alwaysShow = false }) {
  const data = useMemo(() => {
    if (!trends || !trends.labels) return [];
    return trends.labels.map((m, i) => ({
      month: m.slice(-5),
      visits: Number((trends.visits || [])[i] || 0),
      conversions: Number((trends.conversions || [])[i] || 0),
    }));
  }, [trends]);
  const visitsHave = data.some((d) => d.visits > 0);
  const convsHave = data.some((d) => d.conversions > 0);
  if (data.length === 0 && !alwaysShow) return null;
  if (!alwaysShow && !visitsHave && !convsHave) return null;

  return (
    <div className="grid md:grid-cols-2 gap-4 print:grid-cols-1">
      {(visitsHave || alwaysShow) && (
        <TrendCard title="Total website visits"
          subtitle="Combined visits across every property (AWStats / GA4). Long-term lead-generation growth."
          dataKey="visits" data={data} color={primary} testid="view-trend-visits"
          empty={!visitsHave} />
      )}
      {(convsHave || alwaysShow) && (
        <TrendCard title="Total conversions"
          subtitle="Leads, conversions, form submissions and results aggregated across every uploaded marketing channel."
          dataKey="conversions" data={data} color={primary} testid="view-trend-conversions"
          empty={!convsHave} />
      )}
    </div>
  );
}

function TrendCard({ title, subtitle, dataKey, data, color, testid, empty }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" style={{ color }} />
          <div className="font-display font-bold text-sm tracking-tight" style={{ color }}>{title}</div>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1 mb-3">{subtitle}</p>
        <div className="h-48 relative">
          {empty && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-400 z-10 pointer-events-none"
              data-testid={`${testid}-empty`}>
              No data yet for this period
            </div>
          )}
          {data.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={VRP_BAR_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={VRP_AXIS_TICK} />
                <YAxis tick={VRP_AXIS_TICK} tickFormatter={fmtBig} />
                <Tooltip formatter={(v) => fmtBig(v)} />
                <Bar dataKey={dataKey} fill={empty ? "#E4E4E7" : color}>
                  <LabelList dataKey={dataKey} position="top"
                    formatter={(v) => v > 0 ? fmtBig(v) : ""}
                    style={{ fontSize: 9, fill: color }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WorklogReport({ summary, rows, primary }) {
  if (!summary) return null;
  const hasRows = (summary.by_property || []).length > 0;
  const entries = rows || [];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="view-report-worklogs">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4 border-b border-zinc-100 pb-3">
          <div>
            <div className="label-overline">Worklog hours</div>
            <h2 className="font-display font-bold text-xl tracking-tight mt-1">
              Hours delivered this month
            </h2>
          </div>
          <div className="text-right">
            <div className="label-overline">Total hours</div>
            <div className="font-display font-extrabold text-3xl" style={{ color: primary }}
              data-testid="view-report-total-hours">
              {summary.total_hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {summary.total_entries} entr{summary.total_entries === 1 ? "y" : "ies"}
            </div>
          </div>
        </div>

        {hasRows ? (
          <div className="space-y-6">
            {/* Per-property summary */}
            <div>
              <div className="label-overline mb-2">By property</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <th className="text-left py-2 pr-3">Property</th>
                    <th className="text-right py-2 pl-3">Entries</th>
                    <th className="text-right py-2 pl-3">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_property.map((row) => (
                    <tr key={row.business_unit_id} className="border-b border-zinc-100"
                      data-testid={`worklog-row-${row.business_unit_id}`}>
                      <td className="py-2 pr-3 font-semibold">{row.name}</td>
                      <td className="py-2 pl-3 text-right font-mono">{row.entries}</td>
                      <td className="py-2 pl-3 text-right font-mono font-semibold">
                        {Number(row.hours).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detailed entries with full descriptions */}
            {entries.length > 0 && (
              <div data-testid="view-report-worklog-entries">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="label-overline">All entries · {entries.length} row{entries.length === 1 ? "" : "s"}</div>
                  <div className="text-[10px] text-zinc-400 uppercase tracking-wider">
                    Newest first · all descriptions inline
                  </div>
                </div>
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                        <th className="text-left py-2 px-3 w-[100px]">Date</th>
                        <th className="text-left py-2 px-3 w-[140px]">Person</th>
                        <th className="text-left py-2 px-3 w-[150px]">Activity</th>
                        <th className="text-left py-2 px-3">Description</th>
                        <th className="text-right py-2 px-3 w-[70px]">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, idx) => (
                        <tr key={e.id || `${idx}-${e.date}-${e.person}`}
                          className={`align-top ${idx % 2 === 0 ? "" : "bg-zinc-50/30"} border-t border-zinc-100`}
                          data-testid={`worklog-entry-${idx}`}>
                          <td className="py-2 px-3 font-mono text-[11px] text-zinc-700 whitespace-nowrap">
                            {e.date || "—"}
                          </td>
                          <td className="py-2 px-3 text-zinc-800">{e.person || "—"}</td>
                          <td className="py-2 px-3 text-zinc-800">
                            {e.activity || "—"}
                            {e.project && (
                              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider mt-0.5">
                                {e.project}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-zinc-700 whitespace-pre-wrap leading-snug">
                            {e.description || <span className="text-zinc-400">—</span>}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-semibold text-zinc-900 whitespace-nowrap">
                            {Number(e.hours || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-50 border-t border-zinc-200">
                        <td className="py-2 px-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500" colSpan={4}>
                          Total
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-zinc-900">
                          {Number(summary.total_hours).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-zinc-500">No worklog entries uploaded for this month.</p>
        )}
      </CardContent>
    </Card>
  );
}

function PropertyBlock({ block, primary, editing, onPlatformRecEdit }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={`view-property-block-${block.business_unit_id}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-zinc-100 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <Globe2 className="h-4 w-4" style={{ color: primary }} />
              <h2 className="font-display font-extrabold text-2xl tracking-tight">{block.name}</h2>
            </div>
            {block.url && (
              <a href={block.url} target="_blank" rel="noreferrer"
                className="text-xs text-zinc-500 hover:text-zinc-900 font-mono">
                {block.url}
              </a>
            )}
          </div>
          {block.current_visits != null && (
            <div className="text-right">
              <div className="label-overline">Visits</div>
              <div className="font-display font-extrabold text-2xl">{fmtBig(block.current_visits)}</div>
            </div>
          )}
        </div>

        {(block.platform_summaries || block.platforms || []).map((p) => (
          <PlatformStrip key={p.platform} p={p} primary={primary}
            onEdit={onPlatformRecEdit ? (newText) => onPlatformRecEdit(block.business_unit_id, p.platform, newText) : null}
            editing={editing} />
        ))}

        {block.no_data && (
          <p className="text-sm text-zinc-500">No uploads for this property this month.</p>
        )}
      </CardContent>
    </Card>
  );
}

function PlatformStrip({ p, primary, onEdit, editing }) {
  const totals = p.kpis || p.totals || {};
  const entries = Object.entries(totals).slice(0, 8);
  const [draft, setDraft] = useState(p.recommendation || "");
  useEffect(() => { setDraft(p.recommendation || ""); }, [p.recommendation]);
  return (
    <div className="border border-zinc-200 rounded-sm">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-zinc-100 bg-zinc-50">
        <div className="flex items-center gap-2">
          <Badge className="rounded-sm bg-zinc-900 text-white">
            {PLATFORM_LABELS[p.platform] || p.label || p.platform}
          </Badge>
          {p.uploaded_at && (
            <span className="text-[11px] text-zinc-500">Uploaded {formatAuDateTime(p.uploaded_at)}</span>
          )}
        </div>
      </div>
      {entries.length > 0 && (
        // Single-row KPI strip — flex with wrap. Each cell stays at its
        // natural width (min ~96px) so 4-5 KPIs lay out on one line on
        // desktop, gracefully wrapping only when the browser/print width
        // genuinely can't fit them all.
        <div className="flex flex-wrap items-stretch p-3 gap-y-2"
          data-testid={`view-platform-kpis-${p.platform}`}>
          {entries.map(([k, v], i) => (
            <div key={k}
              className={`flex-1 min-w-[96px] py-1 px-2 ${
                i < entries.length - 1 ? "border-r border-zinc-100" : ""
              }`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">{k}</div>
              <div className="font-mono text-sm font-semibold mt-0.5">{fmtBig(v)}</div>
            </div>
          ))}
        </div>
      )}
      {(p.recommendation || (editing && onEdit)) && (
        <div className="border-t border-zinc-100 px-4 py-3 bg-white" data-testid={`view-platform-rec-${p.platform}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-3 w-3" style={{ color: primary }} />
            <div className="label-overline" style={{ color: primary }}>AI Recommendation</div>
          </div>
          {editing && onEdit ? (
            <textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); onEdit(e.target.value); }}
              className="w-full text-xs text-zinc-700 border border-zinc-200 rounded-sm p-2 leading-relaxed font-sans"
              rows={Math.max(3, Math.min(8, (draft || "").split("\n").length + 1))}
              data-testid={`edit-platform-rec-${p.platform}`}
            />
          ) : (
            <p className="text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed">{p.recommendation}</p>
          )}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// Social posts section — Facebook + Instagram organic post snapshot
// =============================================================================
//
// Mirrors the PDF `_social_posts_section` output. In edit mode, admins can:
//   • untick a post to exclude it from the PDF
//   • override the caption text shown in the PDF
// State for both edits is held in the parent ViewReportPage `edits` object
// and persisted via PATCH /reports/{id}.

const POST_METRIC_LABELS_VRP = {
  post_impressions: "Impressions",
  post_impressions_unique: "Reach",
  post_engaged_users: "Engaged",
  post_clicks: "Clicks",
  post_reactions_by_type_total: "Reactions",
  post_video_views: "Video views",
  post_video_avg_time_watched: "Avg watch",
};
const IG_METRIC_LABELS_VRP = {
  engagement: "Engagement", impressions: "Impressions", reach: "Reach",
  saved: "Saves", video_views: "Video views", plays: "Plays",
  likes: "Likes", comments: "Comments", shares: "Shares",
  total_interactions: "Total interactions",
};
const LI_METRIC_LABELS_VRP = {
  impressions: "Impressions", reach: "Reach", clicks: "Clicks",
  engagement: "Engagement",
};

function fmtSpv(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}
function fmtSpDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function SocialPostsSection({ socialPosts, edits, primary, editing,
                              onTogglePost, onCaptionOverride }) {
  const sp = socialPosts || {};
  const fbPosts = sp.facebook || [];
  const igMedia = sp.instagram || [];
  const liPosts = sp.linkedin || [];
  const totalCount = fbPosts.length + igMedia.length + liPosts.length;
  if (totalCount === 0 && !(sp._errors || []).length) {
    return null;
  }

  const excluded = new Set((edits || {}).excluded_ids || []);
  const overrides = (edits || {}).caption_overrides || {};

  // Compose channel-list subtitle only from the platforms with any content
  const chans = [];
  if (fbPosts.length) chans.push("Facebook");
  if (igMedia.length) chans.push("Instagram");
  if (liPosts.length) chans.push("LinkedIn");
  const chanStr = chans.length <= 1
    ? (chans[0] || "Organic social")
    : chans.length === 2
      ? chans.join(" and ")
      : `${chans.slice(0, -1).join(", ")} and ${chans[chans.length - 1]}`;

  // Badge — show every populated channel + total in PDF
  const badgeChips = [];
  if (fbPosts.length) badgeChips.push(`${fbPosts.length} FB`);
  if (igMedia.length) badgeChips.push(`${igMedia.length} IG`);
  if (liPosts.length) badgeChips.push(`${liPosts.length} in`);
  badgeChips.push(`${totalCount - excluded.size} in PDF`);

  return (
    <Card className="rounded-sm border-zinc-200 mt-6" data-testid="vrp-social-posts">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="h-5 w-5" style={{ color: primary }} />
              Social posts · organic
            </h2>
            <div className="text-[12px] text-zinc-500 mt-0.5">
              {chanStr} organic content published during {sp._period_month}.
              {editing ? " · Untick a post to exclude it from the PDF, or override its caption." : ""}
            </div>
          </div>
          <Badge className="rounded-sm font-mono text-[10px]"
            style={{ background: `${primary}1A`, color: primary, border: `1px solid ${primary}33` }}>
            {badgeChips.join(" · ")}
          </Badge>
        </div>

        {(sp._errors || []).length > 0 && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-0.5">
            {sp._errors.map((e) => <div key={e} className="font-mono">· {e}</div>)}
          </div>
        )}

        {fbPosts.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Facebook className="h-3 w-3" /> Facebook · {fbPosts.length} post(s)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {fbPosts.map((p) => (
                <PostCardVRP key={p.id} post={p} platform="facebook"
                  excluded={excluded.has(p.id)}
                  overrideText={overrides[p.id]}
                  editing={editing}
                  onTogglePost={onTogglePost}
                  onCaptionOverride={onCaptionOverride}
                  primary={primary} />
              ))}
            </div>
          </div>
        )}
        {igMedia.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Instagram className="h-3 w-3 text-[#E1306C]" /> Instagram · {igMedia.length} post(s)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {igMedia.map((p) => (
                <PostCardVRP key={p.id} post={p} platform="instagram"
                  excluded={excluded.has(p.id)}
                  overrideText={overrides[p.id]}
                  editing={editing}
                  onTogglePost={onTogglePost}
                  onCaptionOverride={onCaptionOverride}
                  primary={primary} />
              ))}
            </div>
          </div>
        )}
        {liPosts.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Linkedin className="h-3 w-3 text-[#0A66C2]" /> LinkedIn · {liPosts.length} post(s)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {liPosts.map((p) => (
                <PostCardVRP key={p.id} post={p} platform="linkedin"
                  excluded={excluded.has(p.id)}
                  overrideText={overrides[p.id]}
                  editing={editing}
                  onTogglePost={onTogglePost}
                  onCaptionOverride={onCaptionOverride}
                  primary={primary} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PostCardVRP({ post, platform, excluded, overrideText, editing,
                      onTogglePost, onCaptionOverride, primary }) {
  const isFb = platform === "facebook";
  const isLi = platform === "linkedin";
  const isIg = !isFb && !isLi;
  const dateIso = isFb ? post.created_time : post.timestamp;
  let source;
  if (isFb) source = post.page_name || "Facebook";
  else if (isLi) source = post.org_name || "LinkedIn";
  else source = `@${post.ig_username || "instagram"}`;
  const permalink = isFb ? post.permalink_url : post.permalink;
  const thumb = isFb
    ? post.image_url
    : isLi
      ? post.image_url
      : (post.thumbnail_url || post.media_url);
  const baseCaption = (isFb || isLi) ? (post.message || "") : (post.caption || "");
  const text = overrideText !== undefined ? overrideText : baseCaption;
  const ins = post.insights || {};
  const labels = isFb
    ? POST_METRIC_LABELS_VRP
    : isLi ? LI_METRIC_LABELS_VRP : IG_METRIC_LABELS_VRP;
  const isVideo = isIg && (post.media_type === "VIDEO" || post.media_type === "REELS");

  const chips = [];
  if (isFb) {
    if (post.reactions_total !== undefined) chips.push(["Reactions", post.reactions_total, Heart]);
    if (post.comments_total !== undefined) chips.push(["Comments", post.comments_total, MessageCircle]);
    if (post.shares !== undefined) chips.push(["Shares", post.shares, Share2]);
  } else if (isLi) {
    if (post.likes !== undefined && post.likes !== null) chips.push(["Likes", post.likes, Heart]);
    if (post.comments !== undefined && post.comments !== null) chips.push(["Comments", post.comments, MessageCircle]);
    if (post.shares !== undefined && post.shares !== null) chips.push(["Reposts", post.shares, Share2]);
  } else {
    if (post.like_count !== undefined) chips.push(["Likes", post.like_count, Heart]);
    if (post.comments_count !== undefined) chips.push(["Comments", post.comments_count, MessageCircle]);
  }
  Object.entries(ins).forEach(([k, v]) => {
    if (v !== null && v !== undefined && typeof v !== "object") {
      chips.push([labels[k] || k, v, null]);
    }
  });

  return (
    <div className={`rounded-sm border bg-white overflow-hidden flex flex-col transition-opacity ${
      excluded ? "opacity-40 border-zinc-200" : "border-zinc-200"
    }`} data-testid={`vrp-post-${post.id}`}>
      <div className="aspect-video bg-zinc-100 relative">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-300">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        {isVideo && (
          <div className="absolute top-2 right-2 bg-black/60 text-white rounded-sm p-1">
            <PlayCircle className="h-3.5 w-3.5" />
          </div>
        )}
        {editing && (
          <label className="absolute top-2 left-2 bg-white/95 rounded-sm px-2 py-1 text-[11px] flex items-center gap-1.5 cursor-pointer shadow-sm">
            <input type="checkbox"
              checked={!excluded}
              onChange={(e) => onTogglePost(post.id, !e.target.checked)}
              data-testid={`vrp-post-toggle-${post.id}`} />
            {excluded ? "Excluded" : "In PDF"}
          </label>
        )}
        {permalink && (
          <a href={permalink} target="_blank" rel="noreferrer"
            className={`absolute bottom-2 right-2 bg-white/95 rounded-sm p-1.5 ${
              isLi ? "text-zinc-700 hover:text-[#0A66C2]"
                   : "text-zinc-700 hover:text-[#1877F2]"
            }`}
            title={isLi ? "Open on LinkedIn" : "Open on Meta"}>
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="text-[10px] font-mono text-zinc-500 flex items-center justify-between">
          <span className="truncate"><strong style={{ color: primary }}>{source}</strong></span>
          <span>{fmtSpDate(dateIso)}</span>
        </div>
        {editing ? (
          <textarea value={text}
            onChange={(e) => {
              const v = e.target.value;
              if (v === baseCaption) onCaptionOverride(post.id, null);
              else onCaptionOverride(post.id, v);
            }}
            rows={4}
            className="w-full text-[12px] border border-zinc-200 rounded-sm p-2 font-sans resize-y focus:outline-none focus:ring-1 focus:ring-[#1877F2]"
            data-testid={`vrp-post-caption-${post.id}`} />
        ) : (
          <div className="text-[12px] text-zinc-700 whitespace-pre-wrap leading-snug">
            {text || <span className="italic text-zinc-400">(no caption)</span>}
          </div>
        )}
        {chips.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-zinc-100 mt-auto">
            {chips.map(([label, value, Icon]) => (
              <div key={label} className="rounded-sm bg-zinc-50 px-2 py-1 text-[10px] flex items-center justify-between gap-1">
                <span className="text-zinc-500 truncate flex items-center gap-1">
                  {Icon && <Icon className="h-2.5 w-2.5" />} {label}
                </span>
                <span className="font-mono font-semibold text-zinc-900">{fmtSpv(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
